// Core packages
import { formatDate } from "@angular/common";
import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";

// Third party packages
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { ToastrService } from "ngx-toastr";
import { merge, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";

// Custom packages
import { formatRemappingAuthorName } from "src/app/modules/properties/property-mapping-info.utils";
import { PropertyRemappingsService } from "src/app/modules/properties/property-remappings.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { PropertyRemappingItem } from "src/app/shared/models/property/property-remapping.interface";
import { HelperService } from "src/app/shared/services/helper.service";
import { PropertyRemappingsDataSource } from "src/app/shared/utils/data-sources/property-remappings-data-source";
import { AddRemappingDialogComponent } from "../add-remapping-dialog/add-remapping-dialog.component";

/**
 * Modal listing the remapping history of a property with add/edit/delete and
 * export. All operations persist to the backend immediately from this modal.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-remapping-list-dialog",
  templateUrl: "./remapping-list-dialog.component.html",
  styleUrls: ["./remapping-list-dialog.component.scss"],
})
export class RemappingListDialogComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  propertyId: string;
  permissions: string[] = [];
  plantLabels: { [key: string]: string } = {};

  dataSource: PropertyRemappingsDataSource;
  displayedColumns: string[] = [
    "index",
    "date",
    "plant",
    "description",
    "mappingUser",
    "actions",
  ];

  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  constructor(
    public dialogRef: MatDialogRef<RemappingListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private propertyRemappingsService: PropertyRemappingsService
  ) {}

  ngOnInit(): void {
    this.propertyId = this.data.propertyId;
    this.permissions = this.data.permissions || [];
    this.dataSource = new PropertyRemappingsDataSource(
      this.propertyRemappingsService,
      this.helperService
    );

    // Load the plant key -> label map, used by the "plant" table column.
    this.subscriptions.push(
      this.propertyRemappingsService.getPlants().subscribe(
        (res) => {
          if (res && res.status && Array.isArray(res.data)) {
            res.data.forEach((plant: any) => {
              this.plantLabels[plant.key] = plant.label;
            });
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  ngAfterViewInit(): void {
    // Load first page
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0))
    );

    // Reload data on sort or page change
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadPage()))
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    this.dataSource.loadItems(
      this.propertyId,
      start,
      length,
      this.sort.active,
      this.sort.direction
    );
  }

  canCreate(): boolean {
    return this.permissions.includes("canCreateCondoStructures");
  }

  canDelete(): boolean {
    return this.permissions.includes("canDeleteCondoStructures");
  }

  canExport(): boolean {
    return this.permissions.includes("canReadCondoStructuresList");
  }

  /**
   * Compose the label of the "Mappatore" column from the server-resolved
   * author of the record.
   *
   * Returns an empty string when the author is not resolved (`createdBy: null`
   * on legacy records or records whose user no longer exists): the cell is left
   * blank on purpose, no placeholder. Shares the formatting with the
   * "Mappatore" of the property home, which reads the first row of this table.
   *
   * @since 3.0.0
   *
   * @param element Remapping record of the table row
   */
  getMapperName(element: PropertyRemappingItem): string {
    if (!element) {
      return "";
    }
    return formatRemappingAuthorName(element.createdBy);
  }

  onAddRemapping(): void {
    const dialogRef = this.dialog.open(AddRemappingDialogComponent, {
      data: {
        propertyId: this.propertyId,
      },
      width: "600px",
    });
    this.subscriptions.push(
      dialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (res) {
            // A new remapping (server date = today, default sort date desc)
            // lands on the first page: reset the paginator so it is visible.
            if (this.paginator) {
              this.paginator.pageIndex = 0;
            }
            this.loadPage();
          }
        })
    );
  }

  onEditRemapping(element: any): void {
    const dialogRef = this.dialog.open(AddRemappingDialogComponent, {
      data: {
        propertyId: this.propertyId,
        remapping: element,
      },
      width: "600px",
    });
    this.subscriptions.push(
      dialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (res) {
            this.loadPage();
          }
        })
    );
  }

  onDeleteRemapping(element: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "properties.remappings.deleteMessage"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    this.subscriptions.push(
      dialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (!res) {
            return;
          }
          this.subscriptions.push(
            this.propertyRemappingsService
              .delete(this.propertyId, element._id)
              .subscribe(
                (result) => {
                  if (result && result.status) {
                    this.toastrService.success(result.message);
                    this.loadPage();
                  }
                },
                (err) => {
                  this.helperService.handleError(err);
                }
              )
          );
        })
    );
  }

  onExport(): void {
    const date = formatDate(new Date(), "yyyyMMddHHmmss", "en-US");
    const fileName = `${this.translateService.instant(
      "properties.remappings.exportFileName"
    )} ${date}`;
    this.subscriptions.push(
      this.propertyRemappingsService.getExport(this.propertyId).subscribe(
        (res) => {
          const blob = new Blob([res], { type: "application/vnd.ms-excel" });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  onResetFilters(): void {
    this.sort.direction = "desc";
    this.sort.active = "date";
    this.sort._stateChanges.next();
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;
    this.loadPage();
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
